import React, { useState, useEffect } from 'react';
import {
	TextField,
	Checkbox,
	FormControlLabel,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	Switch,
	Typography,
	ToggleButtonGroup,
	ToggleButton,
} from '@mui/material';
import ReactQuill from 'react-quill';
import Grid from '@mui/material/Grid2';
import { Box } from '@mui/system';
import { getAllCategory } from '../../../services/api/Category';

interface wholesale {
	type: 'text' | 'image';
	label: string;
	required: boolean;
}

interface buldDiscount {
	minQuantity: number;
	maxQuantity: number;
	discountPercentage: number;
}

interface ColorVariant {
	colorName: string;
	colorCode: string;
	price: number;
	strickoutPrice: number;
	sizes: Array<{ size: string }>;
	colorImage: string[];
}

interface FormData {
	name: string;
	category: string;
	image: string[];
	description: string;
	price: number;
	quantity: string;
	bestSelling: boolean;
	trending: boolean;
	productImage: string[];
	weight: string;
	weightUnit: string;
	isInquiry: boolean;
	isBulkDiscount: boolean;
	isCustomized: boolean;
	slug?: string;
	isWholesale: boolean;
	wholesaleFields: wholesale[];
	buldDiscountsData: buldDiscount[];
	wholesaleMinQuantity: number;
	productId: string;
	isStickoutPrice: boolean;
	canonicalTag?: any;
	keywords?: any;
	metaDescription?: any;
	metaTitle?: any;
	strickoutPrice: number;
	isColorVariantAvailable: boolean;
	colorVariants: Array<{
		colorName: string;
		colorCode: string;
		price: number;
		strickoutPrice: number;
		sizes: Array<{ size: string }>;
		colorImage: Array<{ url: string; isNew: boolean }>;
	}>;
}

interface ViewProductsProps {
	product: {
		name: string;
		category: string;
		image: string[];
		description: string;
		price: number;
		quantity: string;
		bestSelling: boolean;
		trending: boolean;
		productImage: string[];
		weight: string;
		weightUnit: string;
		isInquiry: boolean;
		isBulkDiscount: boolean;
		isCustomized: boolean;
		slug?: string;
		isWholesale: boolean;
		wholesaleFields: wholesale[];
		buldDiscountsData: buldDiscount[];
		wholesaleMinQuantity: number;
		productId: string;
		isStickoutPrice: boolean;
		strickoutPrice: number;
		isColorVariantAvailable: boolean;
		colorVariants: ColorVariant[];
		canonicalTag?: any;
		keywords?: any;
		metaDescription?: any;
		metaTitle?: any;
	} | null;
}

const sizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const ViewProducts: React.FC<ViewProductsProps> = ({ product }) => {
	const [formData, setFormData] = useState<FormData>({
		name: '',
		description: '',
		category: '',
		price: 1,
		quantity: '',
		bestSelling: false,
		trending: false,
		image: [],
		productImage: [],
		weight: '',
		weightUnit: 'kg',
		slug: product?.slug || '',
		isInquiry: false,
		isBulkDiscount: false,
		isCustomized: false,
		isWholesale: false,
		wholesaleFields: [],
		buldDiscountsData: [],
		wholesaleMinQuantity: 1,
		productId: product?.productId || '',
		isStickoutPrice: false,
		strickoutPrice: 0,
		isColorVariantAvailable: false,
		colorVariants: [],
	});

	const [imagePreviews, setImagePreviews] = useState<string[]>([]);
	const [categories, setCategories] = useState<
		{ categoryName: { name: string }; productCount: number }[]
	>([]);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const data = await getAllCategory();
				setCategories(data?.data || []);
			} catch (error) {
				console.error('Failed to fetch categories:', error);
			}
		};
		fetchCategories();
	}, []);

	useEffect(() => {
		if (product) {
			const validCategory = categories.some(
				(category) => category.categoryName.name === product.category
			)
				? product.category
				: '';

			const transformedColorVariants =
				product.colorVariants?.map((variant) => ({
					colorName: variant.colorName || '',
					colorCode: variant.colorCode || '#ffffff',
					price: variant.price || 0,
					strickoutPrice: variant.strickoutPrice || 0,
					sizes: variant.sizes || [],
					colorImage: variant.colorImage.map((img) => ({
						url: img,
						isNew: false,
					})),
				})) || [];

			setFormData({
				name: product.name,
				description: product.description,
				category: validCategory,
				price: product.price,
				quantity: product.quantity,
				bestSelling: product.bestSelling,
				trending: product.trending,
				image: product.image,
				productImage: product.image,
				weight: product.weight,
				weightUnit: product.weightUnit,
				slug: product.slug,
				isInquiry: product.isInquiry,
				isBulkDiscount: product.isBulkDiscount,
				isCustomized: product.isCustomized,
				isWholesale: product.isWholesale,
				wholesaleFields: product.wholesaleFields,
				buldDiscountsData: product.buldDiscountsData,
				wholesaleMinQuantity: product.wholesaleMinQuantity,
				productId: product.productId,
				isStickoutPrice: product.isStickoutPrice,
				strickoutPrice: product.strickoutPrice,
				isColorVariantAvailable: product.isColorVariantAvailable,
				colorVariants: transformedColorVariants,
			});

			if (product.image && product.image.length > 0) {
				setImagePreviews(product.image.map((value) => value as string));
			}
		}
	}, [product, categories]);

	const getImageUrl = (path: string) => {
		if (!path) return '/assets/image-not-found.webp';
		if (path.startsWith('http')) return path;
		return `${process.env.REACT_APP_BASE_IMAGE_URL}${path}`;
	};

	return (
		<div>
			<form>
				<Grid container spacing={2}>
					<Grid size={{ xs: 12 }}>
						<TextField
							size="small"
							label="Product Name"
							variant="outlined"
							fullWidth
							value={formData.name}
							disabled
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<TextField
							size="small"
							name="slug"
							label="URL Name"
							variant="outlined"
							fullWidth
							value={formData.slug}
							disabled
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<ReactQuill
							value={formData.description}
							readOnly={true}
							modules={{
								toolbar: false,
							}}
							formats={[
								'header',
								'font',
								'size',
								'bold',
								'italic',
								'underline',
								'strike',
								'blockquote',
								'list',
								'bullet',
								'indent',
								'link',
								'image',
								'video',
							]}
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<FormControl fullWidth margin="normal" size="small" sx={{ margin: '0px' }}>
							<InputLabel id="category">Category</InputLabel>
							<Select
								label="Category"
								name="category"
								id="category"
								value={formData.category}
								disabled
								labelId="category"
							>
								<MenuItem value="">Select Category</MenuItem>
								{categories?.map((category, index) => (
									<MenuItem key={index} value={category.categoryName.name}>
										{category.categoryName.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Grid size={{ xs: 12 }}>
						<FormControlLabel
							sx={{ margin: '0px' }}
							control={<Switch checked={formData.isInquiry} disabled />}
							label="Is Enquiry"
						/>
					</Grid>

					{!formData.isInquiry && (
						<>
							<Grid size={{ xs: 12 }}>
								<TextField
									sx={{ margin: '0px' }}
									size="small"
									label="Stock"
									name="quantity"
									type="number"
									value={formData.quantity}
									disabled
									fullWidth
									margin="normal"
								/>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<FormControlLabel
									control={
										<Checkbox
											name="bestSelling"
											checked={formData.bestSelling}
											disabled
										/>
									}
									label="Best Selling"
								/>
								<FormControlLabel
									control={
										<Checkbox
											name="trending"
											checked={formData.trending}
											disabled
										/>
									}
									label="Trending"
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 4 }}>
								<TextField
									sx={{ margin: '0px' }}
									size="small"
									label="Product Price"
									variant="outlined"
									type="number"
									fullWidth
									value={formData.price}
									disabled
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 4 }}>
								<TextField
									sx={{ margin: '0px' }}
									size="small"
									label="Weight"
									name="weight"
									type="number"
									value={formData.weight}
									disabled
									fullWidth
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 4 }}>
								<FormControl fullWidth disabled>
									<InputLabel id="weightUnit-label">Weight Unit</InputLabel>
									<Select
										size="small"
										labelId="weightUnit-label"
										id="weightUnit"
										name="weightUnit"
										value={formData.weightUnit}
										disabled
										label="Weight Unit"
									>
										<MenuItem value="kg">kg</MenuItem>
										<MenuItem value="g">g</MenuItem>
										<MenuItem value="lb">lb</MenuItem>
										<MenuItem value="oz">oz</MenuItem>
									</Select>
								</FormControl>
							</Grid>
						</>
					)}

					{!formData.isInquiry && (
						<Grid size={{ xs: 12 }}>
							<FormControlLabel
								sx={{ margin: '0px' }}
								control={<Switch checked={formData.isWholesale} disabled />}
								label="Is Wholesale"
							/>
							<FormControlLabel
								sx={{ margin: '0px' }}
								control={<Switch checked={formData.isBulkDiscount} disabled />}
								label="Is Bulk Discount"
							/>
						</Grid>
					)}

					{!formData.isInquiry && (
						<>
							{formData.isWholesale && (
								<>
									<Grid size={{ xs: 12 }}>
										<TextField
											sx={{ margin: '0px' }}
											size="small"
											label="Wholesale Minimum Order Quantity"
											name="wholesaleMinQuantity"
											type="number"
											value={formData.wholesaleMinQuantity}
											disabled
											fullWidth
											margin="normal"
										/>
									</Grid>
									<Grid size={{ xs: 12 }}>
										<FormControlLabel
											sx={{ margin: '0px' }}
											control={
												<Switch checked={formData.isCustomized} disabled />
											}
											label="Is Customized"
										/>
									</Grid>
								</>
							)}

							{formData.isCustomized &&
								formData.isWholesale &&
								formData?.wholesaleFields?.length > 0 &&
								formData?.wholesaleFields?.map((value, index) => (
									<React.Fragment key={index}>
										<Grid size={{ xs: 12, md: 4 }}>
											<FormControl fullWidth disabled>
												<InputLabel id={`wholesale-type-${index}`}>
													Field Type
												</InputLabel>
												<Select
													size="small"
													labelId={`wholesale-type-${index}`}
													id={`wholesale-type-id-${index}`}
													value={value.type}
													disabled
													label="Field Type"
												>
													<MenuItem value="text">Text</MenuItem>
													<MenuItem value="image">Image</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid size={{ xs: 12, md: 4 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Label"
												name="label"
												type="text"
												value={value.label}
												disabled
												fullWidth
												margin="normal"
											/>
										</Grid>
										<Grid size={{ xs: 9, sm: 9, md: 3 }}>
											<FormControlLabel
												sx={{ margin: '0px' }}
												control={
													<Switch checked={value.required} disabled />
												}
												label="Is Required"
											/>
										</Grid>
									</React.Fragment>
								))}

							{formData?.isBulkDiscount &&
								formData?.buldDiscountsData?.length > 0 &&
								formData?.buldDiscountsData?.map((val, index) => (
									<React.Fragment key={index}>
										<Grid size={{ xs: 12, sm: 6, md: 4 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Minimum Quantity"
												name="minQuantity"
												type="number"
												value={val.minQuantity}
												disabled
												fullWidth
												margin="normal"
											/>
										</Grid>
										<Grid size={{ xs: 12, sm: 6, md: 4 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Maximum Quantity"
												name="maxQuantity"
												type="number"
												value={val.maxQuantity}
												disabled
												fullWidth
												margin="normal"
											/>
										</Grid>
										<Grid size={{ xs: 9, sm: 9, md: 3 }}>
											<TextField
												sx={{ margin: '0px' }}
												size="small"
												label="Discount Percentage"
												name="discountPercentage"
												type="number"
												value={val.discountPercentage}
												disabled
												fullWidth
												margin="normal"
											/>
										</Grid>
									</React.Fragment>
								))}
						</>
					)}

					<Grid size={{ xs: 12 }}>
						<div style={{ marginBottom: '12px' }}>
							<div>
								{imagePreviews.map((src, index) => (
									<div
										key={index}
										style={{
											display: 'inline-block',
											position: 'relative',
											margin: '5px',
										}}
									>
										<img
											src={getImageUrl(src)}
											alt={`Preview ${index}`}
											style={{ width: '100px', height: '100px' }}
										/>
									</div>
								))}
							</div>
						</div>
					</Grid>

					{formData.isColorVariantAvailable && formData.colorVariants.length > 0 && (
						<Grid size={{ xs: 12 }}>
							<Typography variant="h6" gutterBottom>
								Color Variants
							</Typography>
							{formData.colorVariants.map((variant, variantIndex) => (
								<Grid size={{ xs: 12 }} key={variantIndex}>
									<Box
										sx={{
											p: 3,
											border: '1px solid #eee',
											borderRadius: 1,
											backgroundColor: '#fafafa',
										}}
									>
										<Grid container spacing={2}>
											<Grid size={{ xs: 12, sm: 6 }}>
												<TextField
													fullWidth
													label="Color Name"
													value={variant.colorName}
													disabled
													size="small"
												/>
											</Grid>

											<Grid size={{ xs: 12, sm: 6 }}>
												<TextField
													fullWidth
													label="Color Code"
													value={variant.colorCode}
													disabled
													size="small"
												/>
											</Grid>

											<Grid size={{ xs: 12, sm: 6 }}>
												<TextField
													fullWidth
													label="Price"
													type="number"
													value={variant.price}
													disabled
													size="small"
												/>
											</Grid>

											<Grid size={{ xs: 12, sm: 6 }}>
												<TextField
													fullWidth
													label="Strikout Price"
													type="number"
													value={variant.strickoutPrice}
													disabled
													size="small"
												/>
											</Grid>

											<Grid size={{ xs: 12, sm: 6 }}>
												<Typography variant="subtitle2" gutterBottom>
													Available Sizes
												</Typography>
												<FormControl fullWidth>
													<ToggleButtonGroup
														value={variant.sizes.map(
															(sizeObj) => sizeObj.size
														)}
														disabled
														aria-label="size selection"
														sx={{
															display: 'flex',
															flexWrap: 'wrap',
															gap: 1,
														}}
													>
														{sizes.map((size) => (
															<ToggleButton
																key={size}
																value={size}
																aria-label={size}
																selected={variant.sizes.some(
																	(sizeObj) =>
																		sizeObj.size === size
																)}
																sx={{
																	minWidth: 60,
																	height: 40,
																	padding: '6px 12px !important',
																	borderRadius: '6px !important',
																	border: '1px solid #0C8040 !important',
																	color: 'green',
																	textTransform: 'none',
																	fontWeight: 'bold',
																	'&.Mui-selected': {
																		backgroundColor: '#0C8040',
																		color: 'white',
																		borderColor: '#0C8040',
																		padding: '6px 12px',
																		borderRadius: '6px',
																	},
																	'&.Mui-selected:hover': {
																		backgroundColor: '#006400',
																	},
																	'&:hover': {
																		backgroundColor:
																			'rgba(0,128,0,0.1)',
																	},
																}}
															>
																{size}
															</ToggleButton>
														))}
													</ToggleButtonGroup>
												</FormControl>
											</Grid>

											<Grid size={{ xs: 12 }}>
												<Typography variant="subtitle2" gutterBottom>
													Color Images
												</Typography>
												<Box
													sx={{
														display: 'flex',
														flexWrap: 'wrap',
														gap: 2,
													}}
												>
													{variant.colorImage.map((image, imageIndex) => (
														<Box
															key={imageIndex}
															sx={{
																position: 'relative',
																width: 100,
																height: 100,
																borderRadius: 1,
																overflow: 'hidden',
																border: '1px solid #ddd',
															}}
														>
															<Box
																component="img"
																src={getImageUrl(image.url)}
																alt={`Color ${variantIndex} image ${imageIndex}`}
																sx={{
																	width: '100%',
																	height: '100%',
																	objectFit: 'cover',
																}}
															/>
															<Box
																sx={{
																	position: 'absolute',
																	bottom: 0,
																	left: 0,
																	right: 0,
																	backgroundColor:
																		'rgba(0,0,0,0.5)',
																	color: 'white',
																	fontSize: '0.75rem',
																	textAlign: 'center',
																	py: 0.5,
																}}
															>
																{image.isNew ? 'New' : 'Existing'}
															</Box>
														</Box>
													))}
												</Box>
											</Grid>

											<Grid size={{ xs: 12 }}>
												<Box>
													<Box component="h4">SEO</Box> <br />
													<Box>metaTitle : {product?.metaTitle}</Box>
													<Box>
														metaDescription : {product?.metaDescription}
													</Box>
													<Box>keywords : {product?.keywords}</Box>
													<Box>
														canonicalTag : {product?.canonicalTag}
													</Box>
												</Box>
											</Grid>
										</Grid>
									</Box>
								</Grid>
							))}
						</Grid>
					)}
				</Grid>
			</form>
		</div>
	);
};

export default ViewProducts;
